import { Fragment } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Home, ShoppingBag, MessageCircle, User, Plus, Sprout } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { canSell } from "@/lib/mode";
import { showsGuestFeed } from "@/hooks/useGuestBrowsing";

type NavItem = {
  path: string;
  label: string;
  icon: typeof Home;
  badge?: number;
  matchPaths?: string[];
};

export function BottomNav() {
  const [location, setLocation] = useLocation();
  const { isAuthenticated, user, isAdmin } = useAuth();
  const showCreateFab = canSell(user);

  const { data: unreadMessages = 0 } = useQuery<number>({
    queryKey: ["/api/my/unread-messages"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch("/api/my/unread-messages", { credentials: "include" });
      if (!res.ok) return 0;
      const data = await res.json();
      return Number(data?.count || 0);
    },
    refetchInterval: 30_000,
  });

  // Guests browsing the feed still need a way to move around and a visible
  // route to an account. Without this the nav vanished entirely for logged-out
  // users, which was fine when the feed was behind Landing and isn't now.
  if (!isAuthenticated) {
    if (!showsGuestFeed()) return null;
    return (
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch">
          <button
            type="button"
            onClick={() => setLocation("/")}
            className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium ${
              location === "/" || location.startsWith("/?")
                ? "text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="nav-guest-browse"
          >
            <Home className="h-5 w-5" />
            Browse
          </button>
          <button
            type="button"
            onClick={() => setLocation("/login")}
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-muted-foreground"
            data-testid="nav-guest-signin"
          >
            <User className="h-5 w-5" />
            Sign in
          </button>
        </div>
      </nav>
    );
  }

  const items: NavItem[] = [
    {
      path: "/",
      label: "Browse",
      icon: Home,
      matchPaths: ["/", "/items"],
    },
    {
      path: "/orders",
      label: "Orders",
      icon: ShoppingBag,
      matchPaths: ["/orders", "/order-confirmation", "/checkout", "/deliveries"],
    },
    {
      path: "/messages",
      label: "Messages",
      icon: MessageCircle,
      badge: unreadMessages,
      matchPaths: ["/messages"],
    },
    // /garden renders GardenComingSoon for everyone except admins, so for a
    // normal user this tab was a primary nav slot leading to a placeholder.
    // Hide it until the feature actually ships to non-admins.
    ...(isAdmin
      ? [
          {
            path: "/garden",
            label: "Garden",
            icon: Sprout,
            matchPaths: ["/garden"],
          },
        ]
      : []),
    {
      path: "/account",
      label: "Account",
      icon: User,
      matchPaths: ["/account", "/seller"],
    },
  ];

  const isActive = (item: NavItem) => {
    const paths = item.matchPaths ?? [item.path];
    if (item.path === "/") {
      return location === "/" || location.startsWith("/?");
    }
    return paths.some((p) => location.startsWith(p));
  };

  const fabSlotIndex = Math.ceil(items.length / 2);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
      <div className="flex items-stretch">
        {items.map((item, i) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <Fragment key={item.path}>
              {showCreateFab && i === fabSlotIndex && (
                <div className="relative flex-1">
                  <button
                    type="button"
                    onClick={() => {
                      // Already on the dashboard: setLocation to the same path
                      // with only a different query doesn't reliably re-render
                      // in the wrapped WebView (trap #13), so the ?action=add
                      // mount effect never fires and the button looks dead.
                      // SellerDashboard listens for this event for exactly that
                      // case; the dispatch side had gone missing.
                      if (location.startsWith("/seller")) {
                        window.dispatchEvent(new CustomEvent("stoop:open-quick-add"));
                      } else {
                        setLocation("/seller?action=add");
                      }
                    }}
                    aria-label="Snap a photo to list a product"
                    data-mission-target="seller-fab"
                    className="absolute left-1/2 -translate-x-1/2 -top-5 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background transition active:scale-95"
                  >
                    <Plus className="h-6 w-6" strokeWidth={2.5} />
                  </button>
                  <span className="pointer-events-none absolute left-1/2 top-7 -translate-x-1/2 text-[9px] font-medium text-primary">
                    List
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  // The open map is a history entry stacked on "/" (MapToggle).
                  // Pushing a bare "/" from here would strip the query string
                  // -- the filters -- and strand the map entry. Popping closes
                  // the map back onto the page exactly as it was left.
                  if (item.path === "/" && window.history.state?.stoopMap) {
                    window.history.back();
                    return;
                  }
                  setLocation(item.path);
                }}
                className={`relative flex flex-1 flex-col items-center gap-0 py-1.5 text-[9px] font-medium transition ${
                  item.path === "/garden"
                    ? "text-brand-secondary"
                    : active
                      ? "text-primary"
                      : "text-muted-foreground"
                }`}
              >
                <div className="relative">
                  <Icon className="h-[18px] w-[18px]" />
                  {(item.badge ?? 0) > 0 && (
                    <span className="absolute -right-2 -top-1 inline-flex min-w-[1rem] h-[1rem] items-center justify-center rounded-full bg-red-500 px-0.5 text-[8px] font-bold text-white">
                      {item.badge! > 9 ? "9+" : item.badge}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
                {active && (
                  <div
                    className={`absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full ${
                      item.path === "/garden" ? "bg-brand-secondary" : "bg-primary"
                    }`}
                  />
                )}
              </button>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
}
